import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LanguageService } from '../../shared/services/language.service';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';
import { TenantsService, TenantDto, TenantCreateRequest, TenantUpdateRequest } from '../../shared/services/tenants.service';

const HEX_COLOR_PATTERN = '^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}))?$';
const DOMAIN_SUFFIX_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const FALLBACK_COLOR = '#5b09c7';

@Component({
  standalone: false,
  selector: 'app-manage-tenant-dialog',
  templateUrl: './manage-tenant-dialog.component.html',
  styleUrls: ['./manage-tenant-dialog.component.css']
})
export class ManageTenantDialogComponent implements OnInit, OnDestroy {

  variables: any;
  form!: FormGroup;
  isCreate = false;
  isView = false;

  /** Tracks the validated HEX value shown in the native color picker (updated only when field is valid). */
  mainColorPickerValue = FALLBACK_COLOR;
  secondaryColorPickerValue = FALLBACK_COLOR;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ManageTenantDialogComponent, { refresh?: boolean }>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { title: string; mode: 'create' | 'edit' | 'view'; tenant?: TenantDto },
    private fb: FormBuilder,
    private languageService: LanguageService,
    private tenantsService: TenantsService,
    private infoBar: InfoBarWithStatusService
  ) { }

  ngOnInit(): void {
    this.isCreate = this.data?.mode === 'create';
    this.isView = this.data?.mode === 'view';
    this.buildForm();
    if (this.isView) {
      this.form.disable();
    }
    this.syncColorPickers();
    this.languageService.translations$.pipe(takeUntil(this.destroy$)).subscribe((vars: any) => {
      this.variables = vars;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    const idPattern = '^[a-z0-9][a-z0-9_-]*$';
    if (this.isCreate) {
      this.form = this.fb.group({
        id: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(64), Validators.pattern(idPattern)]],
        name: ['', [Validators.required, Validators.maxLength(200)]],
        logo: ['', [Validators.maxLength(200)]],
        mainColor: ['', [Validators.pattern(HEX_COLOR_PATTERN)]],
        secondaryColor: ['', [Validators.pattern(HEX_COLOR_PATTERN)]],
        useSso: [false],
        maintenancePct: [0, [Validators.min(0), Validators.max(100)]],
        restrictEmailDomain: [false],
        allowedEmailDomainsText: ['']
      });
    } else {
      const t = this.data.tenant!;
      this.mainColorPickerValue = t.mainColor || FALLBACK_COLOR;
      this.secondaryColorPickerValue = t.secondaryColor || FALLBACK_COLOR;
      this.form = this.fb.group({
        id: [{ value: t.id, disabled: true }],
        name: [t.name, [Validators.required, Validators.maxLength(200)]],
        active: [t.active],
        logo: [t.logo ?? '', [Validators.maxLength(200)]],
        mainColor: [t.mainColor ?? '', [Validators.pattern(HEX_COLOR_PATTERN)]],
        secondaryColor: [t.secondaryColor ?? '', [Validators.pattern(HEX_COLOR_PATTERN)]],
        useSso: [!!t.useSso],
        maintenancePct: [t.maintenancePct ?? 0, [Validators.min(0), Validators.max(100)]],
        restrictEmailDomain: [!!t.restrictEmailDomain],
        allowedEmailDomainsText: [this.domainsToText(t.allowedEmailDomains)]
      });
    }
  }

  /**
   * Subscribes to text field changes and updates the native color picker value
   * only when the text is a syntactically valid hex color.
   */
  private syncColorPickers(): void {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    this.form.get('mainColor')!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: string) => {
      if (val && hexRegex.test(val)) {
        this.mainColorPickerValue = val;
      }
    });

    this.form.get('secondaryColor')!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: string) => {
      if (val && hexRegex.test(val)) {
        this.secondaryColorPickerValue = val;
      }
    });
  }

  /** Called when the native color picker fires — pushes the selected HEX into the text control. */
  onColorPickerChange(controlName: 'mainColor' | 'secondaryColor', value: string): void {
    this.form.get(controlName)?.setValue(value, { emitEvent: true });
    this.form.get(controlName)?.markAsTouched();
  }

  save(): void {
    const idCtrl = this.form.get('id');
    const nameCtrl = this.form.get('name');
    if (this.isCreate && idCtrl && typeof idCtrl.value === 'string') {
      idCtrl.setValue(idCtrl.value.trim().toLowerCase(), { emitEvent: false });
    }
    if (nameCtrl && typeof nameCtrl.value === 'string') {
      nameCtrl.setValue(nameCtrl.value.trim(), { emitEvent: false });
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const msg = this.variables?.tenantFormInvalid || this.variables?.mandatoryFieldsMessage || 'Revise el formulario';
      this.infoBar.showSnackbar(msg, ['error-snackbar'], 5000);
      return;
    }

    const restrict = !!this.form.get('restrictEmailDomain')?.value;
    const domainsText = (this.form.get('allowedEmailDomainsText')?.value as string) ?? '';
    const allowedEmailDomains = this.parseDomains(domainsText);
    if (restrict && allowedEmailDomains.length === 0) {
      this.form.get('allowedEmailDomainsText')?.setErrors({ required: true });
      this.form.get('allowedEmailDomainsText')?.markAsTouched();
      const msg = this.variables?.tenantEmailDomainsRequired || this.variables?.tenantFormInvalid;
      this.infoBar.showSnackbar(msg, ['error-snackbar'], 5000);
      return;
    }
    const invalidDomain = allowedEmailDomains.find(d => !DOMAIN_SUFFIX_PATTERN.test(d));
    if (restrict && invalidDomain) {
      this.form.get('allowedEmailDomainsText')?.setErrors({ invalidDomain: true });
      this.form.get('allowedEmailDomainsText')?.markAsTouched();
      const msg = this.variables?.tenantEmailDomainInvalid || this.variables?.tenantFormInvalid;
      this.infoBar.showSnackbar(msg, ['error-snackbar'], 5000);
      return;
    }

    const v = this.form.getRawValue();
    const errMsg = () => this.variables?.tenantSaveFailed || this.variables?.detectedError || 'Error';
    const toOptionalStr = (val: string | null | undefined): string | undefined =>
      val && val.trim() ? val.trim() : undefined;

    if (this.isCreate) {
      const body: TenantCreateRequest = {
        id: (v.id as string).trim(),
        name: (v.name as string).trim(),
        logo: toOptionalStr(v.logo),
        mainColor: toOptionalStr(v.mainColor),
        secondaryColor: toOptionalStr(v.secondaryColor),
        useSso: !!v.useSso,
        maintenancePct: typeof v.maintenancePct === 'number' ? v.maintenancePct : 0,
        restrictEmailDomain: restrict,
        allowedEmailDomains: restrict ? allowedEmailDomains : []
      };
      this.tenantsService.create(body).subscribe({
        next: () => this.dialogRef.close({ refresh: true }),
        error: (err) => {
          console.error('Tenant create failed', err);
          this.infoBar.showSnackbar(errMsg(), ['error-snackbar'], 5000);
        }
      });
    } else {
      const body: TenantUpdateRequest = {
        name: (v.name as string).trim(),
        active: !!v.active,
        logo: toOptionalStr(v.logo),
        mainColor: toOptionalStr(v.mainColor),
        secondaryColor: toOptionalStr(v.secondaryColor),
        useSso: !!v.useSso,
        maintenancePct: typeof v.maintenancePct === 'number' ? v.maintenancePct : 0,
        restrictEmailDomain: restrict,
        allowedEmailDomains: restrict ? allowedEmailDomains : []
      };
      this.tenantsService.update(this.data.tenant!.id, body).subscribe({
        next: () => this.dialogRef.close({ refresh: true }),
        error: (err) => {
          console.error('Tenant update failed', err);
          this.infoBar.showSnackbar(errMsg(), ['error-snackbar'], 5000);
        }
      });
    }
  }

  private domainsToText(domains?: string[]): string {
    if (!domains?.length) {
      return '';
    }
    return domains.map(d => d.replace(/^@/, '')).join('\n');
  }

  /** Parses comma- or newline-separated domain suffixes (e.g. atos.net). */
  private parseDomains(text: string): string[] {
    return text
      .split(/[\n,;]+/)
      .map(d => d.trim().toLowerCase().replace(/^@/, ''))
      .filter(Boolean);
  }
}
